import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../../lib/lang";
import { waLink } from "../../lib/content";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-full border border-white/15 p-0.5 font-mono text-[11px]" data-testid="lang-toggle">
      {["id", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full uppercase transition-colors duration-300 ${lang === l ? "bg-white text-[#0B0E1B]" : "text-slate-300 hover:text-white"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const items = ["services", "work", "reel", "process", "materials", "order"];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled || open ? "bg-[#0B0E1B]/80 backdrop-blur-xl border-white/10" : "border-transparent"}`}>
      <nav className="mx-auto max-w-[1400px] px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Logo />
        <ul className="hidden lg:flex items-center gap-8">
          {items.map((k) => (
            <li key={k}>
              <a href={`#${k}`} data-testid={`nav-link-${k}`} className="text-sm text-slate-300 hover:text-white transition-colors duration-300">
                {t.nav[k]}
              </a>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <LangToggle />
          <a href={waLink(t.waGreeting)} target="_blank" rel="noreferrer" data-testid="nav-dm-button" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#FF2E4D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ff4d66] transition-colors duration-300 shadow-[0_0_24px_rgba(255,46,77,0.45)]">
            <MessageCircle className="w-4 h-4" /> {t.navCta}
          </a>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-white" aria-label="menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.ul initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden overflow-hidden px-5">
            {items.map((k) => (
              <li key={k} className="border-t border-white/10">
                <a href={`#${k}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${k}`} className="block py-4 font-display text-2xl text-white">
                  {t.nav[k]}
                </a>
              </li>
            ))}
            <li className="py-4 border-t border-white/10">
              <a href={waLink(t.waGreeting)} target="_blank" rel="noreferrer" data-testid="mobile-nav-dm-button" className="inline-flex items-center gap-2 rounded-full bg-[#FF2E4D] px-5 py-3 font-semibold text-white">
                <MessageCircle className="w-4 h-4" /> {t.navCta}
              </a>
            </li>
          </motion.ul>
        )}
      </AnimatePresence>
    </header>
  );
};
